/**
 * IFC-Relationen über die Fragments-Daten auflösen – Typen und Öffnungen.
 *
 * <p>Die Relationsnamen sind die des Fragments-Importers, nicht die des IFC-Schemas:
 *
 * <ul>
 *   <li>IfcRelDefinesByType: am Typ `ObjectTypeOf`, am Element `IsDefinedBy` (derselbe Name wie bei
 *       den Eigenschaftssätzen – Typen erkennt man dort nur an ihrer Klasse). Ein `IsTypedBy` gibt
 *       es in den Fragments nicht.</li>
 *   <li>IfcRelVoidsElement: an der Wand `HasOpenings`, an der Öffnung `VoidsElements`.</li>
 *   <li>IfcRelFillsElement: an der Öffnung `HasFillings`, an Tür/Fenster `FillsVoids`.</li>
 *   <li>IfcRelSpaceBoundary (samt 1st/2nd Level): am Raum `BoundedBy`, am Bauteil
 *       `ProvidesBoundaries`.</li>
 * </ul>
 *
 * <p>Öffnungs- und Raumbegrenzungsrelationen stehen nur in Dateien, die mit dem erweiterten
 * Konverter (BIC-BIM-Viewer/api, `createImporter`) erzeugt wurden. Ältere Dateien haben sie nicht –
 * {@link hasOpeningRelations} und {@link hasSpaceBoundaries} erkennen das.
 *
 * <p>Bewusst nur gegen ein schmales Interface statt gegen `FragmentsModel`: so laufen dieselben
 * Funktionen auch gegen ein `SingleThreadedFragmentsModel` in Node und lassen sich an einer echt
 * konvertierten Datei prüfen.
 */
export interface IfcRelationModel {
  getCategories(): Promise<string[]> | string[];
  getItemsOfCategories(categories: RegExp[]): Promise<Record<string, number[]>> | Record<string, number[]>;
  getItemsData(ids: number[], config?: any): Promise<any[]> | any[];
}

export interface RelatedItem {
  localId: number;
  category?: string;
  name?: string;
}

export interface IfcTypeEntry {
  /** Typname, z. B. „T90". */
  name: string;
  /** Typklasse in Großbuchstaben, z. B. IFCDOORTYPE. */
  ifcClass: string;
  /** LocalIds der Elemente dieses Typs. */
  instances: number[];
}

/**
 * `fillings`: Wand → Türen/Fenster in ihren Öffnungen. `hosts`: Tür/Fenster → Wand.
 * `boundaries`: Raum → begrenzende Bauteile. `spaces`: Bauteil → angrenzende Räume.
 */
export type ElementRelation = 'fillings' | 'hosts' | 'boundaries' | 'spaces';

/** Die Fragments-Relationen je Richtung – zwei Schritte bei Öffnungen, einer bei Raumbegrenzungen. */
const RELATION_PATHS: Record<ElementRelation, string[]> = {
  fillings: ['HasOpenings', 'HasFillings'],
  hosts: ['FillsVoids', 'VoidsElements'],
  boundaries: ['BoundedBy'],
  spaces: ['ProvidesBoundaries'],
};

const TYPE_CATEGORY = /(TYPE|STYLE)$/i;
const FILLING_CATEGORY = /^IFC(DOOR|WINDOW)/i;

const exact = (category: string) => new RegExp(`^${category.replace(/[.*+?^${}()|[\]\\]/g, '\\$&')}$`);

/**
 * Die Gegenstücke einer Relation.
 *
 * <p>Die leere Liste ist abgefangen: `getItemsData([])` liefert nicht nichts, sondern ALLE Items
 * des Modells.
 */
export async function relatedItems(model: IfcRelationModel, localIds: number[], relation: string): Promise<RelatedItem[]> {
  if (localIds.length === 0) return [];
  const data = await model.getItemsData(localIds, {
    attributesDefault: false,
    attributes: ['Name'],
    relations: {[relation]: {attributes: true, relations: false}},
    relationsDefault: {attributes: false, relations: false},
  });

  const result = new Map<number, RelatedItem>();
  for (const item of data) {
    for (const related of (item?.[relation] as any[] | undefined) ?? []) {
      const localId = related?._localId?.value;
      if (typeof localId !== 'number' || result.has(localId)) continue;
      result.set(localId, {
        localId,
        category: related?._category?.value,
        name: related?.Name?.value != null ? String(related.Name.value) : undefined,
      });
    }
  }
  return [...result.values()];
}

/** Alle Typobjekte eines Modells mit ihren Elementen. */
export async function typeEntries(model: IfcRelationModel): Promise<IfcTypeEntry[]> {
  const categories = (await model.getCategories()).filter(c => TYPE_CATEGORY.test(c));
  if (categories.length === 0) return [];

  const byCategory = await model.getItemsOfCategories(categories.map(exact));
  const classOf = new Map<number, string>();
  for (const [category, ids] of Object.entries(byCategory)) {
    for (const id of ids ?? []) classOf.set(id, category.toUpperCase());
  }
  if (classOf.size === 0) return [];

  const data = await model.getItemsData([...classOf.keys()], {
    attributesDefault: false,
    attributes: ['Name'],
    relations: {ObjectTypeOf: {attributes: true, relations: false}},
    relationsDefault: {attributes: false, relations: false},
  });

  const entries: IfcTypeEntry[] = [];
  for (const item of data) {
    const localId = item?._localId?.value;
    if (typeof localId !== 'number') continue;
    const instances = ((item.ObjectTypeOf as any[] | undefined) ?? [])
      .map(related => related?._localId?.value)
      .filter((id): id is number => typeof id === 'number');
    const ifcClass = classOf.get(localId) ?? String(item?._category?.value ?? '').toUpperCase();
    // Ein unbenannter Typ bleibt über Klasse und Id ansprechbar, statt zu verschwinden.
    const name = item?.Name?.value != null && String(item.Name.value).trim()
      ? String(item.Name.value).trim()
      : `${ifcClass} ${localId}`;
    entries.push({name, ifcClass, instances});
  }
  return entries;
}

/** Typname je Element – der Typ steckt in `IsDefinedBy` neben den Eigenschaftssätzen. */
export async function typeNamesOf(model: IfcRelationModel, localIds: number[]): Promise<Map<number, string>> {
  const names = new Map<number, string>();
  if (localIds.length === 0) return names;
  const data = await model.getItemsData(localIds, {
    attributesDefault: false,
    // Ohne Attributliste fehlt im Ergebnis auch `_localId` – die Zuordnung waere unmoeglich.
    attributes: ['Name'],
    relations: {IsDefinedBy: {attributes: true, relations: false}},
    relationsDefault: {attributes: false, relations: false},
  });
  for (const item of data) {
    const localId = item?._localId?.value;
    if (typeof localId !== 'number') continue;
    const type = ((item.IsDefinedBy as any[] | undefined) ?? [])
      .find(related => TYPE_CATEGORY.test(String(related?._category?.value ?? '')));
    if (type?.Name?.value != null) names.set(localId, String(type.Name.value));
  }
  return names;
}

/**
 * Elemente über eine IFC-Relation verbinden.
 *
 * <p>Bei Öffnungen geht es über die Öffnung als Zwischenglied; sie selbst gehört nicht ins
 * Ergebnis, sie hat keine Geometrie. Die Klasse kommt mit, damit der Aufrufer filtern kann
 * („die WÄNDE des Raums" – ein Raum wird auch von Türen, Decken und Platten begrenzt).
 */
export async function relatedVia(model: IfcRelationModel, localIds: number[], relation: ElementRelation): Promise<RelatedItem[]> {
  let current: RelatedItem[] = localIds.map(localId => ({localId}));
  for (const step of RELATION_PATHS[relation]) {
    current = await relatedItems(model, current.map(item => item.localId), step);
  }
  return current;
}

/**
 * Ob das Modell Raumbegrenzungen trägt.
 *
 * <p>Ohne Räume gibt es nichts zu begrenzen – das gilt als vorhanden. Sonst genügt ein einziger
 * begrenzter Raum. Fehlen sie ganz, ist entweder die Datei alt konvertiert oder das IFC wurde ohne
 * Space Boundaries exportiert – unterscheiden lässt sich das an der .frag-Datei nicht.
 */
export async function hasSpaceBoundaries(model: IfcRelationModel): Promise<boolean> {
  const spaces = (await model.getItemsOfCategories([/^IFCSPACE$/i]))['IFCSPACE'] ?? [];
  if (spaces.length === 0) return true;
  return (await relatedItems(model, spaces, 'BoundedBy')).length > 0;
}

/**
 * Ob das Modell die Öffnungsrelationen trägt.
 *
 * <p>Ohne Türen und Fenster gibt es nichts zu verbinden – das gilt als vorhanden. Mit Türen oder
 * Fenstern, aber ohne ein einziges IfcOpeningElement, stammt die Datei vom alten Konverter.
 */
export async function hasOpeningRelations(model: IfcRelationModel): Promise<boolean> {
  const categories = (await model.getCategories()).map(c => c.toUpperCase());
  if (categories.includes('IFCOPENINGELEMENT')) return true;
  return !categories.some(c => FILLING_CATEGORY.test(c) && !TYPE_CATEGORY.test(c));
}
